/**
 * TNCY-5 — Administration → Partner Companies page.
 *
 * A tenant_admin manages cross-tenant company links here (XT-D15..D19,
 * XT-D26). The page does four things:
 *
 *   1. **Invite** a partner company → issue a single-use, 7-day code
 *      (POST /invitations). The code is shown **once** to **copy** and share
 *      out-of-band (no auto-send). Outstanding invitations list below
 *      (GET /invitations — metadata-only, survives refresh; TNCY-10) with a
 *      **revoke** control. The code is never re-exposed after creation.
 *   2. **Redeem** a code received out-of-band (POST /invitations/{code}/
 *      redeem) → the partner link is created / reactivated.
 *   3. **Relationships table** (GET /links) — ALL links, active AND
 *      suspended, each showing company name + status + inviting admin
 *      (for invitation links).
 *   4. **Suspend** an active link (PATCH /links/{tenantId}, suspend only).
 *      A suspended row offers **Reactivate**, which issues a fresh
 *      invitation code (mutual re-consent) — the partner redeems it to
 *      reactivate the existing suspended link (XT-D26).
 *
 * The standalone "Invitations" Administration leaf (pending USER invites)
 * moved to the Users page in this same story; the only invitation-
 * flavoured Administration leaf left is this one (tenant links).
 *
 * Distinct from project-level access grants (Layer 2) — those land in
 * TNCY-6. Platform-admin mint is deferred to the platform-admin app.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { ToastProvider, useToast } from "../account/ToastContext.js";
import { ConfirmDialog } from "../admin/shared/ConfirmDialog.js";
import { relativeTime } from "../admin/shared/formatters.js";
import {
  useCreateInvitationMutation,
  useInvitationsQuery,
  useLinksQuery,
  useRedeemInvitationMutation,
  useRevokeInvitationMutation,
  useSuspendLinkMutation,
} from "./hooks.js";
import type { Invitation, InvitationListItem, TenantLink } from "./types.js";
import { CheckIcon, CopyIcon } from "@aeriosiq/ui/icons";

export function PartnerCompaniesPage(): ReactElement {
  return (
    <ToastProvider>
      <PartnerCompaniesPageInner />
    </ToastProvider>
  );
}

function PartnerCompaniesPageInner(): ReactElement {
  const links = useLinksQuery();
  // GET /invitations (TNCY-10) — metadata-only outstanding list, query-backed
  // so it survives a refresh. The one-time code is shown only in the create
  // dialog and is never returned here. create/revoke invalidate this query.
  const invitations = useInvitationsQuery();
  const outstanding = invitations.data?.items ?? [];

  return (
    <Box sx={{ pb: 6 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 3 }}
      >
        <Box>
          <PageHeading>Partner Companies</PageHeading>
        </Box>
        <InviteButton />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Link your company with a partner to share projects and assign work
        across tenants. Invite a company by sharing the code you generate, or
        redeem a code a partner sent you. Links are permanent and can be
        suspended; reactivating a suspended link needs a fresh invitation.
      </Typography>

      <RedeemSection />

      {outstanding.length > 0 ? (
        <OutstandingInvitationsSection items={outstanding} />
      ) : null}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Company relationships
        </Typography>
        {links.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : links.isError ? (
          <Typography color="error">
            Couldn&apos;t load company relationships.
          </Typography>
        ) : links.data.items.length > 0 ? (
          <RelationshipsGrid items={links.data.items} />
        ) : (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
              No partner companies yet
            </Typography>
            <Typography variant="body2">
              Invite a company or redeem a code to create your first link.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/* ─── Invite ──────────────────────────────────────────────────────── */

interface InviteButtonProps {
  /** Optional label override (used by the Reactivate affordance). */
  label?: string;
  /** Render as a small outlined button (row-level affordance). */
  small?: boolean;
}

function InviteButton({
  label = "Invite a company",
  small,
}: InviteButtonProps): ReactElement {
  const { toast } = useToast();
  const create = useCreateInvitationMutation();
  const [codeDialog, setCodeDialog] = useState<Invitation | null>(null);

  async function handleInvite(): Promise<void> {
    try {
      // The create mutation invalidates the outstanding list so the new row
      // appears; the one-time code lives only in this dialog.
      const res = await create.mutateAsync();
      setCodeDialog(res.invitation);
    } catch {
      toast("Couldn't create an invitation. Try again.", "error");
    }
  }

  return (
    <>
      <Button
        variant={small ? "outlined" : "contained"}
        size={small ? "small" : "medium"}
        disabled={create.isPending}
        onClick={() => {
          void handleInvite();
        }}
        {...(create.isPending
          ? { startIcon: <CircularProgress size={14} color="inherit" /> }
          : {})}
      >
        {label}
      </Button>
      <InvitationCodeDialog
        invitation={codeDialog}
        onClose={() => {
          setCodeDialog(null);
        }}
      />
    </>
  );
}

interface InvitationCodeDialogProps {
  invitation: Invitation | null;
  onClose: () => void;
}

function InvitationCodeDialog({
  invitation,
  onClose,
}: InvitationCodeDialogProps): ReactElement {
  return (
    <Dialog
      open={invitation !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="invitation-code-title"
    >
      <DialogTitle id="invitation-code-title">Invitation created</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ mb: 2 }}>
          Share this code with the partner company&apos;s administrator over a
          channel you trust (e.g. email). They redeem it on their own Partner
          Companies page to create the link. The code is single-use and expires
          in 7 days.
        </DialogContentText>
        {invitation ? <CodeField code={invitation.code} /> : null}
        {invitation ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: "block" }}
          >
            Expires {relativeTime(invitation.expiresAt)}.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A read-only code field with a copy-to-clipboard button. */
function CodeField({ code }: { code: string }): ReactElement {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast("Invitation code copied.");
      window.setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      toast("Couldn't copy — select the code and copy it manually.", "error");
    }
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        value={code}
        slotProps={{
          htmlInput: {
            readOnly: true,
            "aria-label": "Invitation code",
            style: { fontFamily: "monospace", letterSpacing: 1 },
          },
        }}
        fullWidth
        size="small"
      />
      <Tooltip title={copied ? "Copied" : "Copy code"}>
        <IconButton
          aria-label="Copy invitation code"
          onClick={() => {
            void copy();
          }}
          edge="end"
        >
          {copied ? (
            <CheckIcon fontSize="small" />
          ) : (
            <CopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

/* ─── Redeem ──────────────────────────────────────────────────────── */

function RedeemSection(): ReactElement {
  const { toast } = useToast();
  const redeem = useRedeemInvitationMutation();
  const [code, setCode] = useState("");

  async function handleRedeem(): Promise<void> {
    const trimmed = code.trim();
    if (trimmed === "") return;
    try {
      const res = await redeem.mutateAsync(trimmed);
      const partner = res.link.partnerTenantName;
      if (res.outcome === "reactivated") {
        toast(`Reactivated the link with ${partner}.`);
      } else if (res.outcome === "noop") {
        toast(`You're already linked with ${partner}.`);
      } else {
        toast(`Linked with ${partner}.`);
      }
      setCode("");
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 404) {
        toast("That code wasn't recognised.", "error");
      } else if (status === 409) {
        toast("That code has already been used, revoked, or expired.", "error");
      } else {
        toast("Couldn't redeem the code. Try again.", "error");
      }
    }
  }

  return (
    <Box
      component="section"
      aria-label="Redeem an invitation code"
      sx={{
        p: 2,
        borderRadius: 1.5,
        border: (t) => `1px solid ${t.palette.divider}`,
        mb: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Have a code from a partner?
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
      >
        <TextField
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleRedeem();
            }
          }}
          placeholder="Paste invitation code"
          size="small"
          slotProps={{
            htmlInput: { "aria-label": "Invitation code to redeem" },
          }}
          sx={{ width: { xs: "100%", sm: 360 } }}
        />
        <Button
          variant="outlined"
          disabled={code.trim() === "" || redeem.isPending}
          onClick={() => {
            void handleRedeem();
          }}
          {...(redeem.isPending
            ? { startIcon: <CircularProgress size={14} color="inherit" /> }
            : {})}
        >
          Redeem
        </Button>
      </Stack>
    </Box>
  );
}

/* ─── Outstanding invitations (GET /invitations — TNCY-10) ────────── */

interface OutstandingInvitationsSectionProps {
  items: InvitationListItem[];
}

function OutstandingInvitationsSection({
  items,
}: OutstandingInvitationsSectionProps): ReactElement {
  const { toast } = useToast();
  const revoke = useRevokeInvitationMutation();
  const [revokeId, setRevokeId] = useState<string | null>(null);

  async function doRevoke(id: string): Promise<void> {
    try {
      await revoke.mutateAsync(id);
      toast("Invitation revoked.");
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 409) {
        toast(
          "That invitation was already redeemed — it can't be revoked.",
          "error",
        );
      } else {
        toast("Couldn't revoke the invitation.", "error");
      }
    } finally {
      setRevokeId(null);
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Outstanding invitations
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Invitation codes are shown only once, when created — they can&apos;t be
        viewed again. To re-share, revoke this one and issue a new invitation.
      </Alert>
      <Box
        component="table"
        role="grid"
        aria-label="Outstanding invitations"
        sx={tableSx}
      >
        <thead>
          <tr role="row">
            <th role="columnheader" scope="col">
              Created
            </th>
            <th role="columnheader" scope="col">
              Expires
            </th>
            <th role="columnheader" scope="col">
              Status
            </th>
            <th role="columnheader" scope="col" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((inv) => (
            <tr
              key={inv.invitationId}
              role="row"
              aria-label={`Invitation ${inv.invitationId}`}
            >
              <td>
                <span title={inv.createdAt}>{relativeTime(inv.createdAt)}</span>
              </td>
              <td>
                <span title={inv.expiresAt}>{relativeTime(inv.expiresAt)}</span>
              </td>
              <td>
                <Chip
                  size="small"
                  label="Outstanding"
                  color="default"
                  variant="outlined"
                />
              </td>
              <td style={{ textAlign: "right" }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setRevokeId(inv.invitationId);
                  }}
                >
                  Revoke
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
      <ConfirmDialog
        open={revokeId !== null}
        title="Revoke invitation?"
        description="The partner won't be able to use this code anymore. You can always issue a new one."
        confirmLabel="Revoke invitation"
        danger
        pending={revoke.isPending}
        onConfirm={() => {
          if (revokeId) void doRevoke(revokeId);
        }}
        onCancel={() => {
          setRevokeId(null);
        }}
      />
    </Box>
  );
}

/* ─── Relationships table ─────────────────────────────────────────── */

interface RelationshipsGridProps {
  items: TenantLink[];
}

function RelationshipsGrid({ items }: RelationshipsGridProps): ReactElement {
  const { toast } = useToast();
  const suspend = useSuspendLinkMutation();
  const [suspendTarget, setSuspendTarget] = useState<TenantLink | null>(null);

  async function doSuspend(link: TenantLink): Promise<void> {
    try {
      await suspend.mutateAsync(link.partnerTenantId);
      toast(`Suspended the link with ${link.partnerTenantName}.`);
    } catch {
      toast("Couldn't suspend the link. Try again.", "error");
    } finally {
      setSuspendTarget(null);
    }
  }

  return (
    <>
      <Box
        component="table"
        role="grid"
        aria-label="Company relationships"
        sx={tableSx}
      >
        <thead>
          <tr role="row">
            <th role="columnheader" scope="col">
              Company
            </th>
            <th role="columnheader" scope="col">
              Status
            </th>
            <th role="columnheader" scope="col">
              Invited by
            </th>
            <th role="columnheader" scope="col">
              Linked
            </th>
            <th role="columnheader" scope="col" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((link) => {
            const suspended = link.status === "suspended";
            const admin = link.invitingAdmin ?? null;
            return (
              <tr
                key={link.linkId}
                role="row"
                aria-label={`${link.partnerTenantName} ${link.status}`}
                data-link-id={link.linkId}
              >
                <td>
                  <Typography variant="body2" fontWeight={500}>
                    {link.partnerTenantName}
                  </Typography>
                </td>
                <td>
                  <Chip
                    size="small"
                    label={suspended ? "Suspended" : "Active"}
                    color={suspended ? "default" : "success"}
                    variant={suspended ? "outlined" : "filled"}
                  />
                </td>
                <td>
                  {admin ? (
                    <Stack spacing={0}>
                      <Typography variant="body2">
                        {admin.firstName} {admin.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {admin.email}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </td>
                <td>
                  <span title={link.createdAt}>
                    {relativeTime(link.createdAt)}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  {suspended ? (
                    <Tooltip title="Issues a new invitation; the partner redeems it to reactivate the link.">
                      <span>
                        <InviteButton small label="Reactivate" />
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setSuspendTarget(link);
                      }}
                    >
                      Suspend
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Box>
      <ConfirmDialog
        open={suspendTarget !== null}
        title="Suspend this link?"
        description={
          suspendTarget
            ? `Suspending the link with ${suspendTarget.partnerTenantName} blocks new shared projects and assignments. Existing access stays intact. There is no resume — to reactivate, you'll issue a new invitation for them to redeem.`
            : ""
        }
        confirmLabel="Suspend link"
        danger
        pending={suspend.isPending}
        onConfirm={() => {
          if (suspendTarget) void doSuspend(suspendTarget);
        }}
        onCancel={() => {
          setSuspendTarget(null);
        }}
      />
    </>
  );
}

/* ─── Shared table styling (mirrors the admin grids) ──────────────── */

const tableSx = {
  width: "100%",
  borderCollapse: "collapse" as const,
  "& th, & td": {
    textAlign: "left" as const,
    py: 1.5,
    px: 1.5,
    borderBottom: (t: { palette: { divider: string } }) =>
      `1px solid ${t.palette.divider}`,
    fontSize: 14,
    verticalAlign: "top" as const,
  },
  "& th": {
    color: "text.secondary",
    fontWeight: 500,
    fontSize: 12,
    textTransform: "uppercase" as const,
    letterSpacing: 0.5,
  },
};
